import { useState } from "react";
import { Link2Off } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useLines } from "@/features/lines/hooks";
import { useCreateChainLink, useDeleteChainLink, useUpstreamLinks } from "./hooks";

// Spec 083 (T076) — "esta linha é alimentada por".
//
// A ligação é SOLTA: declará-la não muda nada na operação da linha. O texto de apoio diz isso, para
// o gestor não hesitar achando que vai amarrar a produção.
//
// Remover é destrutivo o bastante para confirmar, e leve o bastante para não exigir digitar o nome:
// a ligação é recriável em dois cliques, e `ConfirmDialog` com nome digitado existe para o que não
// tem volta.

interface Props {
  lineId: string;
}

export function ChainLinkEditor({ lineId }: Props) {
  const { data: lines = [] } = useLines();
  const query = useUpstreamLinks(lineId);
  const criar = useCreateChainLink(lineId);
  const remover = useDeleteChainLink(lineId);
  const [escolhida, setEscolhida] = useState("");

  const jaLigadas = new Set((query.data?.items ?? []).map((l) => l.upstreamLineId));
  const disponiveis = lines.filter((l) => l.id !== lineId && !jaLigadas.has(l.id));

  async function adicionar() {
    if (!escolhida) return;
    try {
      await criar.mutateAsync(escolhida);
      setEscolhida("");
    } catch (err) {
      toast.error("Não foi possível ligar as linhas.", {
        description: getFriendlyErrorMessage(err),
      });
    }
  }

  async function desligar(upstreamLineId: string, nome: string) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Desfazer a ligação com ${nome}?`,
      description:
        "A cadeia deixa de aparecer no controle de produção. Nenhum dado de produção é afetado.",
      confirmLabel: "Desfazer ligação",
    });
    if (!ok) return;
    try {
      await remover.mutateAsync(upstreamLineId);
    } catch (err) {
      toast.error("Não foi possível desfazer.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-[13px] font-medium text-text-primary">Alimentada por</h3>
        <p className="text-[12px] text-text-secondary">
          Serve ao controle de produção, para ver as etapas da cadeia no mesmo período. Não muda
          nada na operação desta linha.
        </p>
      </div>

      {query.isError ? (
        <Notify.QueryError
          error={query.error}
          onRetry={() => void query.refetch()}
          variant="inline"
        />
      ) : (
        <ul className="space-y-1">
          {(query.data?.items ?? []).map((l) => (
            <li key={l.id} className="flex items-center justify-between gap-3">
              <span className="text-[13px] text-text-primary">{l.upstreamLineName}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Desfazer a ligação com ${l.upstreamLineName}`}
                onClick={() => void desligar(l.upstreamLineId, l.upstreamLineName)}
              >
                <Link2Off className="h-4 w-4" aria-hidden="true" />
              </Button>
            </li>
          ))}
          {(query.data?.items ?? []).length === 0 ? (
            <li className="text-[12px] text-text-tertiary">Nenhuma linha declarada como origem.</li>
          ) : null}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <Select
          aria-label="Linha que alimenta esta"
          value={escolhida}
          onChange={(e) => setEscolhida(e.target.value)}
        >
          <option value="">— Escolher linha —</option>
          {disponiveis.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
        <Button size="sm" disabled={!escolhida || criar.isPending} onClick={() => void adicionar()}>
          Adicionar
        </Button>
      </div>
    </section>
  );
}
